import { ProcessVariableType, ResultElementParameterType, ResultElementSourceType, TableElementSourceType, } from "store/rs-workspace/rs-workspace.types";
import { NoUnitsType } from "store/recipe/recipe.types";
import { cellModelParameterUnitsType, findDictionaryItemByValue, recipeParameterUnitsType, resultElementParameterType, } from "shared/rsDictionary";
import { isNotEmpty } from "utils/validate";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { getSelectedProcessVariable } from "store/global/global.utils";
export const getRowName = (row, simulations, processDatas, bioreactors, optimizations) => {
    switch (row.sourceType) {
        case TableElementSourceType.SIMULATION: {
            const hasOptimizationSelected = isNotEmpty(row.optimizationId);
            const simulation = simulations.find(({ id }) => id === row.sourceId);
            const optimization = hasOptimizationSelected &&
                !!optimizations &&
                optimizations.find(({ id }) => id === row.optimizationId);
            const variant = simulation?.state?.adjustments?.find(({ id }) => id === row.adjustmentId);
            return {
                title: optimization ? optimization.name : (simulation?.name ?? ""),
                subtitle: optimization ? simulation?.name : "",
                subSubtitle: !!variant && variant?.id !== 0 ? variant?.name : "",
            };
        }
        case TableElementSourceType.PROCESS_DATA: {
            const processData = processDatas.find(({ id }) => id === row.sourceId);
            const bioreactor = bioreactors?.find(({ id }) => id === processData?.state.bioreactor?.bioreactorId);
            return {
                title: processData?.name ?? "",
                subtitle: bioreactor?.name ?? "",
            };
        }
        default:
            return {
                title: `${row.sourceType} id-${row.sourceId}`,
            };
    }
};
export const getTypeLabel = (parameter, insights) => {
    switch (parameter.type) {
        case ResultElementParameterType.INSIGHT:
            return `Set ${parameter.inputSetId}, ${getInsightOutputName(parameter, insights)}`;
        default:
            return findDictionaryItemByValue(resultElementParameterType, parameter.type)?.label;
    }
};
export const getColumnLabels = (parameter, processVariables, insights, customVariables, recipes) => {
    let parameterLabel;
    let parameterUnits;
    let unitsDictionary = recipeParameterUnitsType;
    let typeLabel;
    switch (parameter.type) {
        case ResultElementParameterType.ALTERNATIVE_FLOW:
            parameterLabel = parameter.name;
            typeLabel = "Recipe parameter";
            break;
        case ResultElementParameterType.RECIPE:
            parameterLabel = parameter.name;
            parameterUnits = parameter.units;
            break;
        case ResultElementParameterType.CELL_MODEL:
            parameterLabel = parameter.name;
            parameterUnits = parameter.units;
            unitsDictionary = cellModelParameterUnitsType;
            break;
        case ResultElementParameterType.PROCESS_VARIABLE: {
            const isCustomVariable = parameter?.parameter?.type === ProcessVariableType.Custom;
            const { variableType } = { ...parameter?.parameter };
            if (isCustomVariable) {
                const { customVariableId, recipeId } = { ...parameter?.parameter };
                const customVariable = customVariables?.find((customVariable) => customVariable.id === customVariableId && customVariable.recipeId === recipeId);
                const recipeName = recipes?.find(({ id }) => id === customVariable?.recipeId)?.name;
                parameterLabel = `${customVariable?.name ?? ""}${recipeName ? ` (${recipeName})` : ""}`;
                parameterUnits = parseComplexUnitToHtml(customVariable?.formula?.configuredUnits?.complexValue)?.html;
                typeLabel = "Custom variable";
            }
            else {
                parameterLabel = getSelectedProcessVariable(processVariables, variableType)?.name ?? "";
                parameterUnits = parameter?.parameter?.units;
            }
            break;
        }
        case ResultElementParameterType.INSIGHT:
            parameterLabel = getInsightNameById(parameter.insightId, insights);
            break;
    }
    let unitsLabel;
    if (parameter.type === ResultElementParameterType.PROCESS_VARIABLE &&
        parameter?.parameter?.type === ProcessVariableType.Custom) {
        unitsLabel = parameterUnits;
    }
    else {
        unitsLabel =
            parameterUnits !== NoUnitsType.NoUnits
                ? findDictionaryItemByValue(unitsDictionary, parameterUnits)?.label
                : undefined;
    }
    return {
        unitsLabel,
        typeLabel: typeLabel || getTypeLabel(parameter, insights),
        parameterLabel,
    };
};
// Insight
const getInsightNameById = (insightId, insights) => {
    return insights.find(({ id }) => id === insightId)?.name ?? `Insight ${insightId}`;
};
const getInsightOutputName = (parameter, insights) => {
    if (parameter.output.isFinal) {
        return "Final Score";
    }
    const insight = insights.find(({ id }) => id === parameter.insightId);
    const output = insight?.state.outputs.find(({ id }) => id === parameter.output.id);
    if (output) {
        return `${output.type} ${output.id}`;
    }
    return "`${type} ${id}`";
};
export const getSourcesForFilter = (type, rows, currentRowId) => {
    const filteredRows = rows.filter(({ sourceId, id }) => isNotEmpty(sourceId) && id !== currentRowId);
    if (type === ResultElementSourceType.Simulation) {
        const preparedSources = filteredRows
            .filter((row) => row.sourceType === TableElementSourceType.SIMULATION)
            .map((row) => ({
            type: ResultElementSourceType.Simulation,
            simulationId: row.sourceId ?? undefined,
            adjustmentId: row.adjustmentId ?? undefined,
            optimizationId: row.optimizationId ?? undefined,
        }));
        return preparedSources;
    }
    else {
        const preparedSources = filteredRows
            .filter((row) => row.sourceType === TableElementSourceType.PROCESS_DATA)
            .map((row) => ({
            type: ResultElementSourceType.ProcessData,
            processDataId: row.sourceId,
        }));
        return preparedSources;
    }
};
